import { GetServerSideProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { observer } from 'mobx-react'
import { useRouter } from 'next/router'
import {
  FetchSubsidiesValues,
  KeywordSearchGroupForSubsidies,
} from '@components-v2/organisms/KeywordSearchGroupForSubsidies'
import { ISubsidyBase, Language } from '@/types'
import { getQueryValueFromPath, getQueryValueListFromPath, getStaticStores, useStores } from '@/utils'
import { DefaultPageTemplate } from '@components-v2/templates/DefaultPageTemplate'
import { MyHead } from '@components-v2/organisms/MyHead'
import { PageHeader } from '@components-v2/molecules/PageHeader'
import { Button2 } from '@components-v2/atoms/Button2'
import { ConsultingPromotionPopup } from '@components/organisms/ConsultingPromotionPopup'
import { SubsidyCardList } from '@/componentsV2/organisms/SubsidyCardList'
import { SubsidyInquiryGroup } from '@/componentsV2/organisms/SubsidyInquiryGroup'
import { Breadcrumb } from '@/componentsV2/organisms/Breadcrumb'
import { SubsidyFAQGroup } from '@/componentsV2/organisms/SubsidyFAQGroup'
import styles from './index.module.scss'

type Props = {
  ssrQuery: string
  ssrLocationId: string
  ssrSubsidyPurposeIds: string[]
  staticSubsidies: ISubsidyBase[]
  staticHasNextSubsidiesPage: boolean
  ssrPrefName: string
  ssrSubsidyPurposeName: string
}

const NUM_OF_ITEMS = 10

const Subsidies: NextPage<Props> = observer(
  ({
    ssrQuery,
    ssrLocationId,
    ssrSubsidyPurposeIds,
    staticSubsidies,
    staticHasNextSubsidiesPage,
    ssrPrefName,
    ssrSubsidyPurposeName,
  }) => {
    const { t } = useTranslation(['p'])
    const { subsidies } = useStores()
    const router = useRouter()

    const [searchResults, setSearchResults] = useState<ISubsidyBase[]>(
      subsidies.subsidies.length > 0 ? subsidies.subsidies : staticSubsidies
    )
    const [searchKeyword, setSearchKeyword] = useState('')
    const [locationsId, setLocationsId] = useState('')
    const [subsidyPurposeIds, setSubsidyPurposeIds] = useState<string[]>([])
    const [isFetching, setIsFetching] = useState(false)

    const getPageTitle = (prefName: string, purposeName: string): string => {
      // pageTitle にはフリーワード検索の入力ワードは反映させない
      // 入力ワードは今後の Google の検索結果を見て入れるか決める
      const pref = `${prefName}の`
      const purpose = `${purposeName}の`
      const suffix = 'スタートアップ向け補助金・助成金・支援金一覧'

      // 県だけが設定されている場合
      if (prefName && !purposeName) {
        return `${pref}${suffix}`
      }

      // 県と利用目的が設定されている場合
      if (prefName && purposeName) {
        return `${pref}${purpose}${suffix}`
      }

      // 利用目的だけが設定されている場合
      if (!prefName && purposeName) {
        return `${purpose}${suffix}`
      }

      return suffix
    }

    const [pageTitle, setPageTitle] = useState(getPageTitle(ssrPrefName, ssrSubsidyPurposeName))

    const searchHandler = async (input: FetchSubsidiesValues) => {
      const result = await subsidies.fetchSubsidies({
        searchWord: input.keyword,
        locationsId: input.locationsId,
        subsidyPurposeIds: input.subsidyPurposeIds,
        limit: NUM_OF_ITEMS,
        shouldRefresh: true,
      })
      setSearchResults(result)
    }

    const fetchSubsidies = async () => {
      setIsFetching(true)
      const isFirstTimeFetching = staticSubsidies === searchResults
      await subsidies.fetchSubsidies({
        searchWord: searchKeyword,
        locationsId,
        subsidyPurposeIds,
        limit: isFirstTimeFetching ? NUM_OF_ITEMS * 2 : NUM_OF_ITEMS, // 初回の追加読み込みは ISR 分も含めて倍読み込む
        shouldRefresh: isFirstTimeFetching, // 初回の追加読み込みは Store の中身をリセットするために shouldRefresh: true
      })
      setSearchResults(subsidies.subsidies)
      setIsFetching(false)
    }

    const setSearchValues = async (input: FetchSubsidiesValues) => {
      setSearchKeyword(input.keyword)
      setLocationsId(input.locationsId)
      setSubsidyPurposeIds(input.subsidyPurposeIds)
      await searchHandler(input)
    }

    // ブラウザ側での初期化時に Store に hasNextPage を設定
    useEffect(() => {
      subsidies.updateHasNextSubsidiesPage(staticHasNextSubsidiesPage)
    }, [])

    useEffect(() => {
      let isInitial = true
      const handleRouteChange = (path) => {
        const clientQuery = getQueryValueFromPath('query', path)
        const clientLocationId = getQueryValueFromPath('location_id', path)
        const clientSubsidyPurposeIds = getQueryValueListFromPath('subsidy_purpose_id', path)
        const isQueryEqual = ssrQuery === clientQuery
        const isLocationIdEqual = ssrLocationId === clientLocationId
        const isSubsidyPurposeIdIdEqual = arraysHaveSameElements(ssrSubsidyPurposeIds, clientSubsidyPurposeIds)

        // 初回ページ表示時に二重に検索がかかるため、現在のクエリストリングが SSR と同一か確認
        // 同一ならば SSR と表示を変えず、別ならば検索 API を叩く
        if (isQueryEqual && isLocationIdEqual && isSubsidyPurposeIdIdEqual && isInitial) {
          // SSR と同一クエリのため検索処理をスキップ
        } else {
          // 現在のクエリストリングをもとに検索処理
          setSearchValues({
            keyword: clientQuery,
            locationsId: clientLocationId,
            subsidyPurposeIds: clientSubsidyPurposeIds,
          })
          isInitial = false
        }
        if (!(isLocationIdEqual && isSubsidyPurposeIdIdEqual)) {
          // location_id または subsidy_purpose_id が違う場合、タイトル設定処理
          settingPageTitle(clientLocationId, clientSubsidyPurposeIds)
        }
      }
      router.events.on('routeChangeComplete', handleRouteChange)
      return () => {
        router.events.off('routeChangeComplete', handleRouteChange)
      }
    }, [router.events])

    // 二つの配列が同じ要素なら true を返す(要素の順番は考慮しない)
    const arraysHaveSameElements = (array1: string[], array2: string[]) => {
      const set1 = new Set(array1)
      const set2 = new Set(array2)
      if (set1.size !== set2.size) {
        return false
      }
      return array1.every((v) => set2.has(v))
    }

    const fetchPrefName = async (id: string): Promise<string> => {
      return (await subsidies.fetchLocation({ id })).name
    }

    const fetchPurposeName = async (id: string) => {
      return (await subsidies.fetchSubsidyPurpose({ id })).name
    }

    const settingPageTitle = async (clientLocationId: string, clientSubsidyPurposeIds: string[]) => {
      let prefectureName = null
      let subsidyPurposeName = null
      if (clientLocationId) {
        prefectureName = await fetchPrefName(clientLocationId)
      }
      // 利用目的は一つだけの場合のみ利用目的名を設定し、
      // subsidy_purpose_id が複数指定されている場合は null にする
      // 利用目的が複数の場合は SEO 的に不要なのでインデックスさせる必要がない
      if (clientSubsidyPurposeIds.length === 1) {
        subsidyPurposeName = await fetchPurposeName(clientSubsidyPurposeIds[0])
      }
      setPageTitle(getPageTitle(prefectureName, subsidyPurposeName))
    }

    const breadcrumbItems =
      pageTitle !== '補助金・助成金・支援金一覧'
        ? [{ label: '補助金・助成金・支援金を探す' }, { label: pageTitle }]
        : [{ label: '補助金・助成金・支援金を探す' }]

    return (
      <DefaultPageTemplate withContainer={false}>
        <MyHead title={pageTitle || '補助金・助成金・支援金を探す'} />
        <div className={styles.container}>
          <Breadcrumb items={breadcrumbItems} />
          <div className={styles.heading}>
            <PageHeader title={pageTitle || '補助金・助成金・支援金を探す'} />
          </div>
          <div className='container'>
            <div className={styles.searchGroup}>
              <KeywordSearchGroupForSubsidies />
            </div>
            <div className={styles.content}>
              <SubsidyCardList items={searchResults} />
            </div>
            {subsidies.hasNextSubsidiesPage && (
              <div className={styles.button}>
                <Button2
                  size='md'
                  hierarchy='secondary-color'
                  componentType='button'
                  isLoading={isFetching}
                  onClick={fetchSubsidies}
                >
                  {t('+ Load More')}
                </Button2>
              </div>
            )}
          </div>
          <ConsultingPromotionPopup />
        </div>
        <SubsidyInquiryGroup />
        <SubsidyFAQGroup />
      </DefaultPageTemplate>
    )
  }
)

export const getServerSideProps: GetServerSideProps = async ({ locale, query }) => {
  const { subsidies, viewer } = getStaticStores()
  await viewer.changeLanguage(locale as Language)
  // 検索用処理
  const ssrQuery = query.query || ''
  const ssrLocationId = query.location_id || ''
  let ssrSubsidyPurposeIds = []
  if (typeof query.subsidy_purpose_id === 'string') {
    ssrSubsidyPurposeIds = [query.subsidy_purpose_id]
  } else {
    ssrSubsidyPurposeIds = query.subsidy_purpose_id || []
  }
  const staticSubsidies = await subsidies.fetchSubsidies({
    searchWord: ssrQuery as string,
    locationsId: ssrLocationId as string,
    subsidyPurposeIds: ssrSubsidyPurposeIds as string[],
    limit: NUM_OF_ITEMS,
    shouldRefresh: true,
  })
  // タイトル用処理
  let ssrPrefName = ''
  let ssrSubsidyPurposeName = ''
  if (ssrLocationId) {
    ssrPrefName = (await subsidies.fetchLocation({ id: ssrLocationId as string })).name
  }
  if (ssrSubsidyPurposeIds.length === 1) {
    ssrSubsidyPurposeName = (await subsidies.fetchSubsidyPurpose({ id: ssrSubsidyPurposeIds[0] as string })).name
  }

  return {
    props: {
      ssrQuery,
      ssrLocationId,
      ssrSubsidyPurposeIds,
      staticSubsidies,
      staticHasNextSubsidiesPage: subsidies.hasNextSubsidiesPage,
      ssrPrefName,
      ssrSubsidyPurposeName,
      forcedTheme: 'light',
      ...(await serverSideTranslations(locale, ['global', 'search', 'p'])),
    },
  }
}

export default Subsidies
